<template>
	<div class="relative isolate overflow-visible w-full max-w-3xl mx-auto px-6">
		<LoadingSpinner
			v-if="hashValidationLoading"
			class="p-16 mx-auto text-gray-300 h-96"
		/>
		<div
			v-else
			class="flex flex-col pt-12 pb-12 space-y-6 px-4 md:pt-32"
		>
			<template v-if="hashInvalid">
				<div class="flex flex-col w-full max-w-md gap-8 mx-auto">
					<h3 class="text-3xl font-semibold tracking-tight text-center text-gray-900">
						{{ t('forgot-password-hash.invalid.title') }}
					</h3>
					<div class="text-lg text-center">
						{{ t('forgot-password-hash.invalid.message') }}
					</div>
				</div>
			</template>
			<template v-else>
				<h3 class="w-full text-center text-3xl md:text-5xl font-bold text-primary">
					{{ t('forgot-password.h3') }}
				</h3>
				<template v-if="hasHash">
					<div class="text-lg text-center">
						{{ t('forgot-password.expires') }}
					</div>
					<div class="pb-4 text-lg font-bold text-center">
						{{ t('forgot-password.success') }}
					</div>
				</template>
				<div v-else>
					<p class="pb-4 text-lg text-center max-w-lg mx-auto">
						{{ t('forgot-password.enter-email') }}
					</p>
				</div>
			</template>

			<div class="px-4 pb-12 bg-white">
				<UForm
					:id="formId"
					:state="state"
					:schema="schema"
					class="flex flex-col w-full space-y-4 items-center"
					@submit="handleSubmit"
				>
					<div class="flex flex-col sm:flex-row space-y-8 sm:space-x-4 justify-between w-full">
						<UFormField
							v-if="!hasValidHash"
							:label="t('login.username')"
							name="email"
							class="w-full max-w-sm mx-auto"
						>
							<UInput
								:id="FORGOT_EMAIL_INPUT_ID"
								v-model="state.email"
								class="w-full"
								@focus="trackFormEvent($event, FORGOT_PASSWORD_PAGE_LOCATION)"
								@blur="trackFormEvent($event, FORGOT_PASSWORD_PAGE_LOCATION)"
								@input="trackFormEvent($event, FORGOT_PASSWORD_PAGE_LOCATION)"
								@mouseenter="trackFormEvent($event, FORGOT_PASSWORD_PAGE_LOCATION)"
							/>
						</UFormField>
						<template v-else>
							<UFormField
								label="Password"
								name="password"
								class="w-full"
							>
								<UInput
									:id="FORGOT_PASSWORD_INPUT_ID"
									v-model="password"
									:type="showPassword ? 'text' : 'password'"
									:ui="{ trailing: 'pe-1' }"
									class="w-full"
									@focus="trackFormEvent($event, FORGOT_PASSWORD_PAGE_LOCATION)"
									@blur="trackFormEvent($event, FORGOT_PASSWORD_PAGE_LOCATION)"
									@input="trackFormEvent($event, FORGOT_PASSWORD_PAGE_LOCATION)"
									@mouseenter="trackFormEvent($event, FORGOT_PASSWORD_PAGE_LOCATION)"
								>
									<template #trailing>
										<UButton
											color="neutral"
											variant="link"
											size="sm"
											:icon="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
											:aria-label="showPassword ? 'Hide password' : 'Show password'"
											:aria-pressed="showPassword"
											aria-controls="password"
											@click="showPassword = !showPassword"
										/>
									</template>
								</UInput>
								<FormPasswordValidationList
									:password="password"
									class="mt-4"
								/>
							</UFormField>
							<UFormField
								label="Re-enter Password"
								name="passwordConfirmation"
								class="w-full"
							>
								<UInput
									:id="FORGOT_PASSWORD_CONFIRMATION_INPUT_ID"
									v-model="state.passwordConfirmation"
									:type="showPasswordConfirmation ? 'text' : 'password'"
									:ui="{ trailing: 'pe-1' }"
									class="w-full"
									@focus="trackFormEvent($event, FORGOT_PASSWORD_PAGE_LOCATION)"
									@blur="trackFormEvent($event, FORGOT_PASSWORD_PAGE_LOCATION)"
									@input="trackFormEvent($event, FORGOT_PASSWORD_PAGE_LOCATION)"
									@mouseenter="trackFormEvent($event, FORGOT_PASSWORD_PAGE_LOCATION)"
								>
									<template #trailing>
										<UButton
											color="neutral"
											variant="link"
											size="sm"
											:icon="showPasswordConfirmation ? 'i-lucide-eye-off' : 'i-lucide-eye'"
											:aria-label="showPasswordConfirmation ? 'Hide password' : 'Show password'"
											:aria-pressed="showPasswordConfirmation"
											aria-controls="password"
											@click="showPasswordConfirmation = !showPasswordConfirmation"
										/>
									</template>
								</UInput>
							</UFormField>
						</template>
					</div>
					<UButton
						:id="submitButtonId"
						:label="hasValidHash ? t('forgot-password.button-text-reset') : t('forgot-password.button-text-send-reset')"
						:loading="loading"
						:disabled="formDisabled"
						size="xl"
						type="submit"
						color="primary"
						class="w-full max-w-sm flex justify-center uppercase mt-8 sm:mt-0"
						@click="trackFormEvent($event, FORGOT_PASSWORD_PAGE_LOCATION)"
						@mouseenter="trackFormEvent($event, FORGOT_PASSWORD_PAGE_LOCATION)"
						@focus="trackFormEvent($event, FORGOT_PASSWORD_PAGE_LOCATION)"
					/>
				</UForm>
				<NuxtLink
					:id="FORGOT_BACK_TO_LOGIN_BUTTON_ID"
					to="/login"
					class="flex items-center text-primary text-sm w-full max-w-sm mx-auto mt-4"
					@click="trackEvent($event, FORGOT_PASSWORD_PAGE_LOCATION)"
					@mouseenter="trackEvent($event, FORGOT_PASSWORD_PAGE_LOCATION)"
					@focus="trackEvent($event, FORGOT_PASSWORD_PAGE_LOCATION)"
				>
					<UIcon
						name="i-lucide:chevron-left"
						class="text-primary"
					/>
					<span>
						{{ t('forgot-password.back-to-login') }}
					</span>
				</NuxtLink>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useErrorReporter } from '@/composables/useErrorReporter'
import { useGA4Tracking } from '@/composables/useGA4Tracking'
import { SUCCESS } from '@/constants/form/states.js'
import { EMAIL } from '@/constants/form/text-inputs/types.js'
import { API_BASE } from '@/constants/index.js'
import {
	inputFieldStateValidator, inputPasswordConfirmValidator, inputPasswordValidator
} from '@/validators/text-inputs.js'
import { useRoute } from 'vue-router'
import * as z from 'zod'

import {
	FORGOT_BACK_TO_LOGIN_BUTTON_ID,
	FORGOT_EMAIL_INPUT_ID,
	FORGOT_PASSWORD_CONFIRMATION_INPUT_ID,
	FORGOT_PASSWORD_FORM_ID,
	FORGOT_PASSWORD_INPUT_ID,
	FORGOT_PASSWORD_PAGE_LOCATION,
	FORGOT_PASSWORD_RESET_FORM_ID,
	FORGOT_PASSWORD_RESET_SUBMIT_BUTTON_ID,
	FORGOT_PASSWORD_SUBMIT_BUTTON_ID,
	PASSWORD_FORGOT_FORM_TYPE,
	PASSWORD_RESET_FORM_TYPE
} from '@/constants/ga4/tracking-ids/forms/forgot-password'

const route = useRoute()

const {
	trackFormSubmit,
	trackFormEvent,
	trackEvent
} = useGA4Tracking()

const { t } = useI18n({
	useScope: 'global'
})

const { bugsnagReport } = useErrorReporter()

const toast = useToast()

const loading = ref(false)
const password = ref('')
const hashInvalid = ref(false)
const hashValidationLoading = ref(true)
const showPassword = ref(false)
const showPasswordConfirmation = ref(false)

const hash = computed(() => {
	return route.params.hash || ''
})

const hasHash = computed(() => hash.value !== '')
const hasValidHash = computed(() => hasHash.value && !hashInvalid.value)



const emailSchema = z.object({
	email: z.string().email(t('login.invalid-email'))
})

const passwordSchema = z.object({
  passwordConfirmation: z.string().refine(val => val === password.value, t('password-confirm-error-message'))
})

const schema = computed(() => (hasHash.value ? passwordSchema : emailSchema))

type Schema = z.infer<typeof emailSchema | typeof passwordSchema>


const state = reactive<Partial<Schema>>({
	email: '',
	passwordConfirmation: ''
})

onMounted(async () => {
	if (!hasHash.value) {
		hashValidationLoading.value = false
		return
	}

	await $fetch(`${API_BASE}/auth/password/reset/${hash.value}`, {
		method: 'GET',
		credentials: 'include',
		onResponse({ response }) {
			if (!response.ok) {
				throw new Error(response._data?.errors?.[0]?.title || 'Error: Invalid login credentials')
			}
			if (!response._data?.isValid) {
				hashInvalid.value = true
			}
		}
	}).catch(e => {
		bugsnagReport({
			error: e.message,
			showToast: true,
			toast: {
				title: t('forgot-password.error-toast-title'),
				message: e.message
			}
		})
	}).finally(() => {
		hashValidationLoading.value = false
	})
})

const isResetForm = computed(() => hasValidHash.value)

const submitButtonId = computed(() => {
	return isResetForm.value ? FORGOT_PASSWORD_RESET_SUBMIT_BUTTON_ID : FORGOT_PASSWORD_SUBMIT_BUTTON_ID
})

const formId = computed(() => {
	return isResetForm.value ? FORGOT_PASSWORD_RESET_FORM_ID : FORGOT_PASSWORD_FORM_ID
})

const formType = computed(() => {
	return isResetForm.value ? PASSWORD_RESET_FORM_TYPE : PASSWORD_FORGOT_FORM_TYPE
})

const formDisabled = computed(() => {
	const passwordsValidated = inputPasswordValidator(password.value, state.email) === SUCCESS
		&& inputPasswordConfirmValidator(state.passwordConfirmation, password.value) === SUCCESS
	return isResetForm.value ? !passwordsValidated : !(inputFieldStateValidator(state.email, EMAIL) === SUCCESS)
})

const formData = computed(() => {
	if (isResetForm.value) {
		return {
			password: password.value,
			passwordAgain: state.passwordConfirmation
		}
	}
	return {
		email: state.email || ''
	}
})

const apiEndpoint = computed(() => {
	if (isResetForm.value) {
		return `${API_BASE}/auth/password/reset/${hash.value}`
	}
	return `${API_BASE}/auth/password/forgot`
})

const submit = async () => {
	loading.value = true
	await $fetch(apiEndpoint.value, {
		method: 'POST',
		credentials: 'include',
		body: formData.value,
		onResponse({ response }) {
			if (!response.ok) {
				throw new Error(response._data?.errors?.[0]?.title || 'Error: Invalid login credentials')
			}
			if (response.status === 204 || response.status === 200) {
				handleSuccessfulResponse()
			}
		}
	}).catch(e => {
		bugsnagReport({
			error: e.message,
			showToast: true,
			toast: {
				title: t('forgot-password.error-toast-title'),
				message: e.message
			}
		})
	}).finally(() => {
		loading.value = false
	})
}

const formSubmitParameters = computed(() => {
	return isResetForm.value
		? {
			has_password: !!password.value,
			has_password_confirmation: !!state.passwordConfirmation,
			passwords_match: password.value === state.passwordConfirmation
		}
		: {
			has_email: !!state.email
		}
})

const handleSuccessfulResponse = () => {
	trackFormSubmit(formId.value, formType.value, FORGOT_PASSWORD_PAGE_LOCATION, formSubmitParameters.value)

	if (isResetForm.value) {
		toast.add({
			title: t('forgot-password.success-toast-title'),
			description: t('forgot-password.success-toast-title'),
			color: 'success'
		})
		setTimeout(() => {
			navigateTo('/login')
		}, 1000)
	} else {
		toast.add({
			title: t('forgot-password.success-toast-title'),
			description: t('forgot-password.success-toast-message-send-reset'),
			color: 'success'
		})
	}
}

// Tracking functions
const handleSubmit = async () => {
	trackFormSubmit(formId.value, formType.value, FORGOT_PASSWORD_PAGE_LOCATION, formSubmitParameters.value)

	await submit()
}
</script>
